import { useMutation } from '@apollo/client/react'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { TextField, Theme, makeStyles } from '@material-ui/core'
import {
    CreateTrackTipComment,
    UpdateTrackTipComment,
} from 'api/graphQL/mutations/change.mutations'
import {
    addTrackChangeComment,
    updateTrackChangeComment,
} from 'api/graphQL/reactiveVars/element'
import {
    CreateTrackChangeCommentInput,
    UpdateTrackChangeCommentInput,
} from 'models/changes'
import React, { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { CommentButtons } from './CommentButtons'
import useBetaWorkspaceToggle from './hooks/useBetaWorkspaceToggle'
import { useResizeObserver } from './hooks/useResizeObserver'
import { HeightChangeCallback } from './types/heightManagement.types'

export interface CommentSectionStylesProps {
    isBetaWorkspaceToggleEnabled?: boolean
}

const useStyles = makeStyles<Theme, CommentSectionStylesProps>(
    (theme: Theme) => ({
        textField: {
            borderRadius: pxToRem(4),
            border: (props) =>
                props.isBetaWorkspaceToggleEnabled
                    ? '1px solid #BDBDBD'
                    : '1px solid #C9C9C9',
            minHeight: pxToRem(32),
            width: pxToRem(200),
            lineHeight: pxToRem(14),
            padding: 0,
            marginBottom: theme.spacing(0.5),
        },
        inputRoot: {
            '&::placeholder': {
                fontSize: pxToRem(12),
                color: (props) =>
                    props.isBetaWorkspaceToggleEnabled ? '#C8C8C8' : '#8D8D8D',
            },

            fontSize: pxToRem(12),
            padding: theme.spacing(0.625, 1.25),
        },
        cancelButton: {
            borderRadius: pxToRem(100),
            fontSize: pxToRem(12),
            backgroundColor: theme.palette.common.white,
            border: '1px solid #C9C9C9',
            padding: 0,
            '&:hover': {
                backgroundColor: theme.palette.common.white,
                border: '1px solid #C9C9C9',
            },
        },
        replyButton: {
            borderRadius: pxToRem(100),
            fontSize: pxToRem(12),
            backgroundColor: '#A78966',
            color: theme.palette.common.white,
            border: '1px solid #A78966',
            padding: 0,
            '&:hover': {
                backgroundColor: '#A78966',
                color: theme.palette.common.white,
                border: '1px solid #A78966',
            },
        },
        disabledReplyButton: {
            border: '1px solid #C9C9C9',
        },
        buttonContainer: {
            paddingTop: theme.spacing(1.25),
        },
        commentSectionContainer: {
            position: 'relative',
            zIndex: 2,
            // Ensure comment section stays within the card's stacking context
            backgroundColor: 'transparent',
        },
    })
)

export interface CommentSection extends HeightChangeCallback {
    trackChangeId: string
    elementId: string
    familyId: string
    commentId?: string
    text?: string
    handleEdit?: () => void
}

export const CommentSection = ({
    trackChangeId,
    elementId,
    familyId,
    commentId,
    text,
    handleEdit,
    onHeightChange,
}: CommentSection) => {
    const isBetaWorkspaceToggleEnabled = useBetaWorkspaceToggle()
    const styleProps = React.useMemo(() => ({ isBetaWorkspaceToggleEnabled }), [
        isBetaWorkspaceToggleEnabled,
    ])

    const classes = useStyles(styleProps)
    const { projectId } = useParams<{ projectId?: string }>()
    const inputRef = React.useRef<HTMLInputElement>()
    const containerRef = React.useRef<HTMLDivElement>()

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [addComment] = useMutation<{ input: CreateTrackChangeCommentInput }>(
        CreateTrackTipComment,
        {
            onCompleted: (data: any) => {
                addTrackChangeComment(data.createTrackChangeComment)
                setInputValue('')
                setShowButtons(false)
                setAreButtonsDisabled(true)
            },
            onError: () => {
                showGlobalSnackbar({
                    variables: {
                        message: `There was a saving your comment.`,
                        variant: 'error',
                        id: 'CommentSection_AddCommentError',
                    },
                })
            },
        }
    )

    const [updateComment] = useMutation<{
        input: UpdateTrackChangeCommentInput
    }>(UpdateTrackTipComment, {
        onCompleted: (data: any) => {
            handleEdit?.()
            // Use requestAnimationFrame to ensure the reactive state update happens after component re-renders
            requestAnimationFrame(() => {
                updateTrackChangeComment(data.updateTrackChangeComment)
                requestAnimationFrame(() => {
                    onHeightChange?.()
                })
            })
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: `There was a updating your comment.`,
                    variant: 'error',
                    id: 'CommentSection_AddCommentError',
                },
            })
        },
    })
    const isEdit = React.useMemo(() => !!text && text !== '', [text])

    const [showButtons, setShowButtons] = React.useState<boolean>(
        isEdit ?? false
    )
    const [areButtonsDisabled, setAreButtonsDisabled] = React.useState<boolean>(
        !isEdit
    )
    const [inputValue, setInputValue] = React.useState<string>(text ?? '')
    const heightChangeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

    const triggerDebouncedHeightChange = React.useCallback(() => {
        if (heightChangeTimeoutRef.current) {
            clearTimeout(heightChangeTimeoutRef.current)
        }

        heightChangeTimeoutRef.current = setTimeout(() => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    onHeightChange?.()
                })
            })
        }, 50)
    }, [onHeightChange])

    const { observeElement } = useResizeObserver({
        onResize: () => {
            if (isEdit) {
                onHeightChange?.()
            } else {
                triggerDebouncedHeightChange()
            }
        },
        debounceMs: isEdit ? 0 : 50,
        enabled: !!onHeightChange,
    })

    // Update input value when text prop changes (for editing comments)
    React.useEffect(() => {
        setInputValue(text ?? '')
        if (text && text !== '') {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    onHeightChange?.()
                })
            })
        }
    }, [text, onHeightChange])

    // Cleanup timeout on unmount
    React.useEffect(() => {
        return () => {
            if (heightChangeTimeoutRef.current) {
                clearTimeout(heightChangeTimeoutRef.current)
            }
        }
    }, [])

    const handleOnFocus = useCallback(() => {
        if (!isEdit) {
            setShowButtons(true)
        }
        // Always trigger height remeasurement when focused (for both new and edit)
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                onHeightChange?.()
                if (isEdit) {
                    setTimeout(() => {
                        requestAnimationFrame(() => {
                            onHeightChange?.()
                        })
                    }, 100)
                }
            })
        })
    }, [onHeightChange, isEdit])

    const handleOnChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value
            setInputValue(value)

            if (value.length >= 1) {
                setAreButtonsDisabled(false)
            } else {
                setAreButtonsDisabled(isEdit ? false : true) // Keep enabled for edit mode
            }

            if (isEdit) {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        onHeightChange?.()
                    })
                })
            } else {
                // Use debounced height change for new comments
                triggerDebouncedHeightChange()
            }
        },
        [triggerDebouncedHeightChange, isEdit, onHeightChange]
    )

    const handleCancel = useCallback(() => {
        if (!isEdit) {
            setInputValue('')
            setShowButtons(false)
            // Trigger height remeasurement when buttons disappear
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    onHeightChange?.()
                })
            })
        } else {
            handleEdit?.()
            // Trigger height remeasurement when exiting edit mode
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    onHeightChange?.()
                })
            })
        }
    }, [handleEdit, isEdit, onHeightChange])

    const handleReplyButton = useCallback(() => {
        if (!isEdit) {
            addComment({
                variables: {
                    input: {
                        comment: inputValue,
                        familyId,
                        trackChangeId,
                        elementId,
                        projectId: projectId as string,
                    },
                },
            })
        } else {
            updateComment({
                variables: {
                    input: {
                        comment: inputValue,
                        trackChangeId,
                        id: commentId,
                        projectId: projectId as string,
                    },
                },
            })
        }
    }, [
        trackChangeId,
        elementId,
        familyId,
        projectId,
        inputValue,
        isEdit,
        commentId,
        addComment,
        updateComment,
    ])

    // Trigger height measurement when buttons show/hide state changes
    React.useEffect(() => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                onHeightChange?.()
            })
        })
    }, [showButtons, onHeightChange])

    // Trigger height measurement when component mounts
    React.useEffect(() => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                onHeightChange?.()
            })
        })
    }, [onHeightChange])

    // Trigger height measurement when entering/exiting edit mode
    React.useEffect(() => {
        if (isEdit) {
            setShowButtons(true)
            setAreButtonsDisabled(false)
            // Trigger height measurement when switching to edit mode
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    onHeightChange?.()
                })
            })
        }
    }, [isEdit, onHeightChange])

    // Observe container and input elements for size changes
    React.useEffect(() => {
        if (containerRef.current) {
            observeElement(containerRef.current)
        }
        if (inputRef.current) {
            observeElement(inputRef.current)
        }
    }, [observeElement, inputValue])

    // Add specific input event listener for edit mode
    React.useEffect(() => {
        if (!inputRef.current || !isEdit || !onHeightChange) return

        const inputElement = inputRef.current

        const handleInput = () => {
            // Immediate height measurement on input changes during edit
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    onHeightChange()
                })
            })
        }

        inputElement.addEventListener('input', handleInput)
        inputElement.addEventListener('paste', handleInput)
        inputElement.addEventListener('keydown', handleInput)

        return () => {
            inputElement.removeEventListener('input', handleInput)
            inputElement.removeEventListener('paste', handleInput)
            inputElement.removeEventListener('keydown', handleInput)
        }
    }, [isEdit, onHeightChange])

    return (
        <GridContainer
            direction="column"
            data-testid="commentSection"
            className={classes.commentSectionContainer}
            ref={containerRef}
        >
            <GridItem>
                <TextField
                    data-testid="commentTextField"
                    InputProps={{
                        classes: {
                            root: classes.textField,
                            input: classes.inputRoot,
                        },
                        margin: 'none',
                    }}
                    inputProps={{
                        maxLength: 300,
                        'data-testid': 'commntTextFieldInner',
                    }}
                    onClick={handleOnFocus}
                    multiline
                    variant="outlined"
                    size="small"
                    placeholder={
                        !isEdit
                            ? isBetaWorkspaceToggleEnabled
                                ? 'Add a reply..'
                                : 'Add a reply'
                            : undefined
                    }
                    inputRef={inputRef}
                    onChange={handleOnChange}
                    value={inputValue}
                />
            </GridItem>
            {showButtons && (
                <CommentButtons
                    leftButtonText={!isEdit ? 'Reply' : 'Save'}
                    rightButtonText="Cancel"
                    leftButtonActionCallback={handleReplyButton}
                    rightButtonActionCallback={handleCancel}
                    areButtonsDisabled={areButtonsDisabled}
                />
            )}
        </GridContainer>
    )
}
